import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { SessionProvider } from 'next-auth/react';
import { appWithTranslation } from 'next-i18next';
import { Session } from 'next-auth';
import initModal from '~/services/modal.service';
import { AppProvider } from '~/store/store';
import Maintenance from './maintenance';
import getRuntimeConfig from '~/services/runtime-config.service';
import Spinner from '~/components/common/spinner';
import { AppProps } from 'next/app';
import useCheckMaintenance from '~/hooks/check-maintenance';
import { useAppInsights } from '~/hooks/app-insights';
import GoogleTagManager from '~/components/common/gtm';
import { RuntimeConfig } from '~/models/runtime-config';
import { CXPThemeProvider, GlobalStyle } from '@ops-gsic/cxp-component-library-react';
import { urlWithBasePath } from '~/utils/url-helpers';
import Recaptcha from '~/components/common/recaptcha';

initModal();

function AppWrappper({ children }: { children: React.ReactNode }) {
    const [runtimeConfig, setRuntimeConfig] = useState<RuntimeConfig>();
    const { message, loading } = useCheckMaintenance({ app: 'CXP' });

    useAppInsights();

    useEffect(() => {
        getRuntimeConfig().then(runtimeConfig => {
            setRuntimeConfig(runtimeConfig);
        });

        // when back button is clicked, make sure new routed page is scrolled to top
        window.history.scrollRestoration = 'manual';
    }, []);

    const maintenanceMode = runtimeConfig?.maintenance === 'true';
    const gtmTrackingId = runtimeConfig?.gtmID as string;

    return (
        <>
            <GoogleTagManager gtmID={gtmTrackingId} />
            <Recaptcha />
            {typeof maintenanceMode === 'undefined' || loading ? (
                <Spinner isLoading />
            ) : maintenanceMode || message?.isActive ? (
                <Maintenance message={message} />
            ) : (
                children
            )}
        </>
    );
}

function CxpApp({ Component, pageProps }: AppProps) {
    const nextAuthApiBasePAth = `${process.env.CXP_APP_BASE_PATH}/api/auth`.replace(/\/+/g, '/');

    return (
        <SessionProvider
            session={(pageProps as any).session as Session}
            refetchInterval={180}
            refetchOnWindowFocus
            basePath={nextAuthApiBasePAth}
        >
            <CXPThemeProvider defaultTheme="light">
                <GlobalStyle fontsPath={urlWithBasePath('/fonts')} />
                <AppProvider>
                    <Head>
                        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
                        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                        <meta name="format-detection" content="telephone=no" />
                    </Head>
                    <AppWrappper>
                        {/* With this approach, all hooks inside AppWrapper have access to initialized store. Otherwise, hooks may be executed before the providers are ready */}
                        <Component {...pageProps} />
                    </AppWrappper>
                </AppProvider>
            </CXPThemeProvider>
        </SessionProvider>
    );
}

export default appWithTranslation(CxpApp);
